import React, { Fragment, useEffect, useMemo, useState } from "react"
import { connect } from "react-redux"
import compose from "ramda/src/compose"
import { filter, get, includes, isEmpty, map, toLower, toUpper } from "lodash"
import { Formik, Form, Field } from "formik"
import PropTypes from "prop-types"

import { maskTelCustom } from "services/utils/mask"
import ENV from "../../envProperties"

import { getProductsSharedData } from "services/state/actions"
import { TELEFONIA_MOVEL } from "services/state/billing/constants"
import { STATUS } from "./constants"

import DefaultTitle from "shared/components/DefaultTitle"
import Container from "shared/components/DefaultContainer/index"
import Typograph from "components/Typograph/"
import Button from "shared/components/Button/Button"
import SelectSearchable from "shared/components/SelectSearchable/index"
import EmptyState from "shared/components/EmptyState/index"
import imageOk from "assets/svg/icon-ok.svg"

import styles from "./styles.css"
import Link from "shared/components/Link/index"
import WrapperSkeleton from "components/WrapperSkeleton"

const SharedData = ({ getProductsSharedData, productsSharedData }) => {
  const [selectedAssetTag, setSelectedAssetTag] = useState("")
  const [selectedProduct, setSelectedProduct] = useState(null)

  const getLineOfBussines = (product) => {
    return toUpper(
      get(product, "lineOfBusiness", get(product, "productLine.name", ""))
    )
  }

  const getDescription = (product) =>
    get(product, "assetFullName", get(product, "description", ""))

  const getPhone = (product) =>
    get(product, "assetTag", get(product, "serviceId", ""))

  const getPlan = (product) => {
    const match = getDescription(product).match(/Plano\s+(.*)$/)
    return match ? match[1].trim() : ""
  }

  function getDataLimit(product) {
    const description = getDescription(product)
    const match = description.match(/(\d+\s*(?:GB|Tera|TB))/i)
    return match ? match[1].replace(/\s+/g, "") : null
  }

  const handleOpenSharedData = (phone) => {
    window.open(
      `${ENV.BFF}/auth/oauth2/authorization?destination=sharedData&location=${ENV.API_SHARED_DATA}/${phone}`,
      "_blank"
    )
  }

  const productsData = filter(
    get(productsSharedData, "products", []),
    (product) => {
      return (
        getLineOfBussines(product) === TELEFONIA_MOVEL &&
        includes(toLower(getDescription(product)), "algar dados") &&
        includes(toLower(getDescription(product)), "plano compartilhado") &&
        get(product, "status", "") !== STATUS.CANCELADO
      )
    }
  )

  const productOptions = useMemo(() => {
    return map(productsData, (product) => ({
      value: get(product, "assetTag", ""),
      label: maskTelCustom(getPhone(product))
    }))
  }, [productsData])

  const isLoading = productsSharedData.loading
  const hasData = !isEmpty(productsData)
  const showEmptyState = !isLoading && !hasData

  useEffect(() => {
    getProductsSharedData()
  }, [getProductsSharedData])

  return (
    <Fragment>
      <DefaultTitle className={styles.title}>
        Compartilhamento de Dados
      </DefaultTitle>

      <WrapperSkeleton path="sharedData" isChildrenReady={!isLoading}>
        {hasData && (
          <Container>
            <Typograph
              htmlTag="h2"
              size="medium"
              weight="bold"
              color="outer_space"
              classNames={styles.mb18}
            >
              Celular Algar
            </Typograph>

            <div className={styles.content}>
              <div>
                <Formik
                  enableReinitialize
                  initialValues={{ lineOfBusiness: "", productAssetTag: "" }}
                  onSubmit={() => {
                    setSelectedProduct(
                      productsData.find(
                        (product) => product.assetTag === selectedAssetTag
                      )
                    )
                  }}
                  render={() => (
                    <Form className={styles.form}>
                      <div className={styles.selectContainer}>
                        <Field
                          id="assetTag"
                          name="assetTag"
                          component={SelectSearchable}
                          options={productOptions}
                          value={selectedAssetTag}
                          label="Selecione o seu número de celular"
                          placeholder={false}
                          onChange={(e) => setSelectedAssetTag(e.value)}
                        />
                      </div>

                      <Button type="submit" disabled={!selectedAssetTag}>
                        Adicionar contato
                      </Button>
                    </Form>
                  )}
                />
              </div>

              {selectedProduct && (
                <div className={styles.box}>
                  <div>
                    <Typograph
                      htmlTag="p"
                      size="small"
                      weight="regular"
                      color="outer_space"
                    >
                      Celular Algar
                    </Typograph>

                    <Typograph
                      htmlTag="span"
                      size="medium"
                      weight="bold"
                      color="outer_space"
                    >
                      {maskTelCustom(getPhone(selectedProduct))}
                    </Typograph>
                  </div>

                  <div>
                    <Typograph
                      htmlTag="p"
                      size="small"
                      weight="regular"
                      color="outer_space"
                    >
                      Plano
                    </Typograph>

                    <Typograph
                      htmlTag="span"
                      size="medium"
                      weight="bold"
                      color="outer_space"
                    >
                      {getPlan(selectedProduct)}
                    </Typograph>
                  </div>

                  <div>
                    <Typograph
                      htmlTag="p"
                      size="small"
                      weight="regular"
                      color="outer_space"
                    >
                      Cota
                    </Typograph>

                    <Typograph
                      htmlTag="span"
                      size="medium"
                      weight="bold"
                      color="outer_space"
                    >
                      {getDataLimit(selectedProduct)}
                    </Typograph>
                  </div>

                  <Button
                    className={styles.button}
                    onClick={() =>
                      handleOpenSharedData(getPhone(selectedProduct))
                    }
                  >
                    Gerenciar dados
                  </Button>
                </div>
              )}
            </div>
          </Container>
        )}

        {showEmptyState && (
          <EmptyState
            isContainer
            showTitle={false}
            icon={imageOk}
            title="Tudo certo!"
            description="Atualmente, não encontramos produtos para compartilhamento de dados."
          >
            <Link className={styles.btnEmptyState} to="/">
              Ir para página inicial
            </Link>
          </EmptyState>
        )}
      </WrapperSkeleton>
    </Fragment>
  )
}

SharedData.propTypes = {
  getProductsSharedData: PropTypes.func,
  productsSharedData: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.bool,
    data: PropTypes.object,
    products: PropTypes.array
  })
}

const mapStateToProps = (state) => ({
  productsSharedData: state.productsSharedData
})

const enhance = compose(connect(mapStateToProps, { getProductsSharedData }))

export default enhance(SharedData)



// WEBPACK FOOTER //
// ./src/scenes/SharedData/index.js